import { enableProdMode, importProvidersFrom, isDevMode, LOCALE_ID } from "@angular/core";

import { AppTitleStrategy } from "@/app/services/title-strategy-service";
import { registerLocaleData } from "@angular/common";
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from "@angular/common/http";
import localeEs from "@angular/common/locales/es";
import { AngularFireModule } from "@angular/fire/compat";
import {
	DateFnsAdapter,
	MAT_DATE_FNS_FORMATS,
	provideDateFnsAdapter
} from "@angular/material-date-fns-adapter";
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from "@angular/material/core";
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from "@angular/material/form-field";
import { MAT_SELECT_CONFIG } from "@angular/material/select";
import { MatSnackBarModule } from "@angular/material/snack-bar";
import { bootstrapApplication, BrowserModule } from "@angular/platform-browser";
import { provideAnimations } from "@angular/platform-browser/animations";
import { provideRouter, Routes, TitleStrategy, withComponentInputBinding } from "@angular/router";
import {
	MSAL_GUARD_CONFIG,
	MSAL_INSTANCE,
	MSAL_INTERCEPTOR_CONFIG,
	MsalBroadcastService,
	MsalGuard,
	MsalGuardConfiguration,
	MsalInterceptor,
	MsalInterceptorConfiguration,
	MsalService
} from "@azure/msal-angular";
import {
	BrowserCacheLocation,
	InteractionType,
	IPublicClientApplication,
	LogLevel,
	PublicClientApplication
} from "@azure/msal-browser";
import { FontAwesomeModule } from "@fortawesome/angular-fontawesome";
import { provideTransloco } from "@jsverse/transloco";
import { provideNgIconsConfig } from "@ng-icons/core";
import { es } from "date-fns/locale";
import "moment/locale/es";
import { provideCharts, withDefaultRegisterables } from "ng2-charts";
import "skeleton-elements/css";
import { AppComponent } from "./app/app.component";
import { HttpErrorsInterceptor } from "./app/http-interceptors/errors.interceptor";
import { HttpHeadersInterceptor } from "./app/http-interceptors/headers.interceptor";
import { AuthGuardService } from "./app/services/auth/auth-guard.service";
import { ValidateHashChangePasswordGuardService } from "./app/services/auth/validate-hash-chage-password-guard.service";
import { environment } from "./environments/environment";
import { TranslocoHttpLoader } from "./transloco-loader";

const APP_ROUTES: Routes = [
	{
		path: "home",
		title: "Inicio",
		loadComponent: () => import("./app/main/home/home.component").then((m) => m.HomeComponent),
		canActivate: [AuthGuardService]
	},
	{
		path: "nodo",
		title: "Nodo",
		loadComponent: () =>
			import("./app/main/ticket-form/ticket-form.component").then((m) => m.TicketFormComponent),
		canActivate: [AuthGuardService]
	},
	{
		path: "app",
		title: "App",
		loadComponent: () =>
			import("./app/main/ticket-form/ticket-form.component").then((m) => m.TicketFormComponent),
		canActivate: [AuthGuardService]
	},
	{
		path: "lineas-telefono",
		title: "Líneas telefónicas",
		loadComponent: () =>
			import("./app/main/ticket-form/ticket-form.component").then((m) => m.TicketFormComponent),
		canActivate: [AuthGuardService]
	},
	{
		path: "terminales",
		title: "Terminales",
		loadComponent: () =>
			import("./app/main/terminales-form/terminales-form.component").then((m) => m.TerminalesFormComponent),
		canActivate: [AuthGuardService]
	},
	{
		path: "login",
		title: "Login",
		loadComponent: () => import("./app/main/login/login.component").then((m) => m.LoginComponent)
	},
	{
		path: "security-login",
		title: "Verificación en dos pasos",
		loadComponent: () =>
			import("./app/main/login/security-login/security-login.component").then(
				(m) => m.SecurityLoginComponent
			),
		canActivate: [AuthGuardService]
	},
	{
		path: "rememberPassword/:idUser/:date/:hash",
		title: "Recordar contraseña",
		loadComponent: () =>
			import("./app/main/remember-password/remember-password.component").then(
				(m) => m.RememberPasswordComponent
			),
		canActivate: [ValidateHashChangePasswordGuardService]
	},
	{
		path: "forbidden",
		title: "Acceso denegado",
		loadComponent: () =>
			import("./app/main/corely/error/forbidden/forbidden.component").then((m) => m.ForbiddenComponent)
	},
	{
		path: "**",
		redirectTo: "home"
	}
];

if (environment.production) {
	enableProdMode();
}

//Establece los pipes en formato ES (number con formato coma en decimales pe.)
registerLocaleData(localeEs);

// START LOGIN MICROSOFT (https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/samples/msal-angular-v3-samples/angular-standalone-sample/src/main.ts)
export function loggerCallback(logLevel: LogLevel, message: string) {
	console.log(message);
}

const isIE =
	window.navigator.userAgent.indexOf("MSIE ") > -1 || window.navigator.userAgent.indexOf("Trident/") > -1;

export function MSALInstanceFactory(): IPublicClientApplication {
	return new PublicClientApplication({
		auth: {
			//LOC
			// clientId: '05ed4d55-24ad-4e1b-83f9-53f5588adbe9',
			// authority: 'https://login.microsoftonline.com/common',
			// redirectUri: 'http://localhost:4200/'

			// PRO
			clientId: "e293d2ef-4781-4324-aa6d-b9ec11e4f8b6",
			authority: "https://login.microsoftonline.com/common",
			redirectUri: window.location.protocol + "//" + window.location.host
		},
		cache: {
			cacheLocation: BrowserCacheLocation.LocalStorage,
			storeAuthStateInCookie: isIE // set to true for IE 11
		},
		system: {
			loggerOptions: {
				loggerCallback,
				logLevel: LogLevel.Info,
				piiLoggingEnabled: false
			}
		}
	});
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
	const protectedResourceMap = new Map<string, Array<string>>();
	protectedResourceMap.set("https://graph.microsoft.com/v1.0/me", ["user.read"]);

	return {
		interactionType: InteractionType.Popup,
		protectedResourceMap
	};
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
	return {
		interactionType: InteractionType.Popup,
		authRequest: {
			scopes: ["user.read"]
		}
	};
}
// END LOGIN MICROSOFT

const appConfig = {
	providers: [
		importProvidersFrom(
			BrowserModule,
			FontAwesomeModule,
			AngularFireModule.initializeApp(environment.firebase),
			MatSnackBarModule
		),
		{ provide: TitleStrategy, useClass: AppTitleStrategy },
		{ provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: { appearance: "outline" } },
		{
			provide: MAT_SELECT_CONFIG,
			useValue: { overlayPanelClass: "corely-select-overlay-pane", panelWidth: null }
		},
		provideCharts(withDefaultRegisterables()),
		provideNgIconsConfig({ size: "24px" }),
		{ provide: LOCALE_ID, useValue: "es" },
		provideDateFnsAdapter({
			parse: {
				dateInput: [
					"D/M/YYYY",
					"DD/M/YYYY",
					"D/MM/YYYY",
					"DD/MM/YYYY",
					"D-M-YYYY",
					"DD-M-YYYY",
					"D-MM-YYYY",
					"DD-MM-YYYY",
					"YYYY-MM-DD"
				]
			},
			display: {
				dateInput: "DD/MM/YYYY",
				monthYearLabel: "MMMM YYYY",
				dateA11yLabel: "LL",
				monthYearA11yLabel: "MMMM YYYY"
			}
		}),
		{ provide: DateAdapter, useClass: DateFnsAdapter, deps: [MAT_DATE_LOCALE] },
		{ provide: MAT_DATE_LOCALE, useValue: es },
		{ provide: MAT_DATE_FORMATS, useValue: MAT_DATE_FNS_FORMATS },
		{
			provide: HTTP_INTERCEPTORS,
			useClass: HttpHeadersInterceptor,
			multi: true
		},
		{
			provide: HTTP_INTERCEPTORS,
			useClass: HttpErrorsInterceptor,
			multi: true
		},
		provideHttpClient(withInterceptorsFromDi()),
		provideAnimations(),
		provideRouter(APP_ROUTES, withComponentInputBinding()),
		{
			provide: HTTP_INTERCEPTORS,
			useClass: MsalInterceptor,
			multi: true
		},
		{
			provide: MSAL_INSTANCE,
			useFactory: MSALInstanceFactory
		},
		{
			provide: MSAL_GUARD_CONFIG,
			useFactory: MSALGuardConfigFactory
		},
		{
			provide: MSAL_INTERCEPTOR_CONFIG,
			useFactory: MSALInterceptorConfigFactory
		},
		MsalService,
		MsalGuard,
		MsalBroadcastService,
		provideHttpClient(),
		provideTransloco({
			config: {
				availableLangs: [
					{ id: "es", label: "Español" },
					{ id: "en", label: "English" }
				],
				defaultLang: "es",
				reRenderOnLangChange: true,
				prodMode: !isDevMode()
			},
			loader: TranslocoHttpLoader
		})
	]
};

bootstrapApplication(AppComponent, appConfig).catch((err) => console.error(err));
